import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { Phone, Mail, MapPin, ChevronDown, Wrench, Paintbrush, Hammer, Menu, X, ArrowUp, Send, CheckCircle, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/265e2178-063a-4b28-b2d1-679a3ad35f4d/images/df0db8fd4f015bf84c87939e43e4745a8b1cc7198a7948902850221787d62a7b.png";
const ABOUT_IMG = "https://static.prod-images.emergentagent.com/jobs/265e2178-063a-4b28-b2d1-679a3ad35f4d/images/f8c3ebb17b8d1902bc4b7555ef666b01153cfabaf48a3e828471ee7576a00419.png";

const CATEGORIES = [
  {
    name: "Hırdavat",
    desc: "Vida, somun, cıvata, menteşe ve tüm bağlantı elemanları. Her projeniz için doğru parça.",
    img: "https://images.unsplash.com/photo-1770386582823-3a7094e35b22?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxib3NjaCUyMHN0YW5sZXklMjB0b29sc3xlbnwwfHx8fDE3NzYyNzg3OTN8MA&ixlib=rb-4.1.0&q=85",
    icon: <Wrench className="w-6 h-6" />,
    span: "md:col-span-7 md:row-span-2",
  },
  {
    name: "El Aletleri",
    desc: "Profesyonel el aletleri, ölçüm aletleri ve aksesuar çeşitleri.",
    img: "https://static.prod-images.emergentagent.com/jobs/265e2178-063a-4b28-b2d1-679a3ad35f4d/images/d2bbaef75b5431d5600b093ffaf53072a4395e96586107bbafab24e4222ee1ea.png",
    icon: <Hammer className="w-6 h-6" />,
    span: "md:col-span-5 md:row-span-1",
  },
  {
    name: "Nalburiye",
    desc: "Kilit, anahtar, boru, tesisat malzemeleri ve günlük ihtiyaçlarınız.",
    img: "https://images.pexels.com/photos/22873527/pexels-photo-22873527.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    icon: <Wrench className="w-6 h-6" />,
    span: "md:col-span-5 md:row-span-1",
  },
  {
    name: "Boya Malzemeleri",
    desc: "İç cephe, dış cephe boyaları, fırça, rulo ve boya aksesuarları.",
    img: "https://static.prod-images.emergentagent.com/jobs/265e2178-063a-4b28-b2d1-679a3ad35f4d/images/6479d748b5382a2d07b9a62bbd21a3fc2a7f02f13d9d5c9206521402991dcb85.png",
    icon: <Paintbrush className="w-6 h-6" />,
    span: "md:col-span-12 md:row-span-1",
  },
];

const BRANDS = ["Filli Boya", "Bosch", "Stanley", "Makita", "Karcher", "Knipex", "Hilti", "DYO", "Marshall", "DeWalt"];

/* ===== NAVIGATION ===== */
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: "Ana Sayfa", href: "#hero" },
    { label: "Hakkımızda", href: "#about" },
    { label: "Ürünler", href: "#categories" },
    { label: "Markalar", href: "#brands" },
    { label: "İletişim", href: "#contact" },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-slate-950/80 border-b border-white/10 shadow-lg shadow-black/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <a href="#hero" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src="https://customer-assets.emergentagent.com/job_quality-hardware-4/artifacts/pr5qa15e_Gemini_Generated_Image_dd7x2tdd7x2tdd7x.png"
            alt="Tuğra Yapı Market Logo"
            className="h-16 w-auto object-contain drop-shadow-lg"
          />
        </a>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-10">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-").replace(/ü/g, "u").replace(/ı/g, "i").replace(/İ/g, "i").replace(/ş/g, "s").replace(/ö/g, "o").replace(/ç/g, "c").replace(/ğ/g, "g").replace(/i̇/g, "i")}-link`}
              className="nav-link text-sm font-semibold tracking-wider uppercase text-slate-400 hover:text-slate-50 transition-colors"
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-slate-50 p-2"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-slate-950/95 backdrop-blur-xl border-b border-white/10 pb-6">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className="block px-6 py-3 text-sm font-semibold tracking-wider uppercase text-slate-400 hover:text-amber-500 hover:bg-slate-900/50 transition-colors"
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
    </nav>
  );
};

/* ===== HERO SECTION ===== */
const Hero = () => (
  <section id="hero" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
    {/* Background Image */}
    <div className="absolute inset-0 -z-10">
      <img
        src={HERO_IMG}
        alt="Tuğra Yapı Market"
        className="w-full h-full object-cover object-center"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/75 to-slate-950/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-70" />
    </div>

    {/* Content */}
    <div className="hero-content max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
      <div className="max-w-2xl">
        <h1
          className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-[0.9] text-slate-50 mb-8"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          Kalite ve
          <br />
          <span className="text-amber-500">Güven</span>in
          <br />
          Adresi
        </h1>
        <p className="text-lg text-slate-400 leading-relaxed max-w-lg mb-10">
          Hırdavat, el aletleri, nalburiye ve boya malzemelerinde yılların deneyimi ile
          Türkiye'nin güvenilir yapı market adresi.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <a
            href="#categories"
            data-testid="hero-cta-products"
            className="inline-flex items-center justify-center bg-amber-600 text-slate-950 font-bold px-8 py-4 uppercase tracking-wider hover:bg-amber-500 transition-colors text-sm"
          >
            Ürünlerimiz
          </a>
          <a
            href="#contact"
            data-testid="hero-cta-contact"
            className="inline-flex items-center justify-center border border-slate-700 bg-transparent text-slate-50 px-8 py-4 uppercase tracking-wider hover:bg-slate-800 transition-colors text-sm"
          >
            Bize Ulaşın
          </a>
        </div>
      </div>
    </div>

    {/* Scroll Indicator */}
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-500">
      <span className="text-[10px] tracking-[0.3em] uppercase">Keşfet</span>
      <ChevronDown className="w-4 h-4 animate-bounce" />
    </div>
  </section>
);

/* ===== ABOUT SECTION ===== */
const About = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      data-testid="about-section"
      className="relative py-24 md:py-32 overflow-hidden grain-overlay"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-20 items-center">
          {/* Image Side */}
          <div className={`lg:col-span-5 ${visible ? "slide-in-left" : "opacity-0"}`}>
            <div className="relative">
              <div className="absolute -inset-4 border border-amber-600/20" />
              <img
                src={ABOUT_IMG}
                alt="Tuğra Yapı Market Geçmişi"
                className="w-full h-[400px] md:h-[500px] object-cover"
                loading="lazy"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-950 to-transparent h-24" />
            </div>
          </div>

          {/* Text Side */}
          <div className={`lg:col-span-7 ${visible ? "fade-in-up" : "opacity-0"}`}>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-amber-500 mb-4">
              Hakkımızda
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-50 mb-8"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Yılların Deneyimi,
              <br />
              Değişmeyen Kalite
            </h2>
            <div className="divider-amber mb-8" />
            <p className="text-base leading-relaxed text-slate-400 mb-6">
              Tuğra Yapı Market, yıllardır hizmet veren güvenilir bir yapı market olarak,
              müşterilerine Türkiye genelinde en kaliteli ürünleri sunmayı ilke edinmiştir.
              Hırdavat, nalburiye, el aletleri ve boya malzemeleri alanında geniş ürün
              yelpazemiz ile her türlü ihtiyacınıza çözüm üretiyoruz.
            </p>
            <p className="text-base leading-relaxed text-slate-400 mb-10">
              Türkiye'nin önde gelen markalarını bünyemizde bulunduruyor, profesyonel
              ve bireysel kullanıcıların beklentilerini en üst düzeyde karşılıyoruz.
              Müşteri memnuniyeti ve kaliteli hizmeti ön planda tutarak, güvene dayalı
              ilişkiler kuruyoruz.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 border-t border-slate-800 pt-8">
              <div data-testid="stat-experience">
                <span className="stat-number text-3xl sm:text-4xl text-amber-500">20+</span>
                <p className="text-xs tracking-[0.15em] uppercase text-slate-500 mt-2">Yıl Deneyim</p>
              </div>
              <div data-testid="stat-products">
                <span className="stat-number text-3xl sm:text-4xl text-amber-500">5000+</span>
                <p className="text-xs tracking-[0.15em] uppercase text-slate-500 mt-2">Ürün Çeşidi</p>
              </div>
              <div data-testid="stat-customers">
                <span className="stat-number text-3xl sm:text-4xl text-amber-500">10K+</span>
                <p className="text-xs tracking-[0.15em] uppercase text-slate-500 mt-2">Mutlu Müşteri</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ===== CATEGORIES SECTION ===== */
const Categories = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="categories"
      ref={sectionRef}
      data-testid="categories-section"
      className="py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-amber-500 mb-4">
            Ürün Kategorileri
          </p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-50"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            İhtiyacınız Olan
            <br />
            Her Şey Burada
          </h2>
        </div>

        {/* Bento Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 ${visible ? "" : "opacity-0"}`}>
          {CATEGORIES.map((cat, i) => (
            <div
              key={cat.name}
              data-testid={`category-${cat.name.toLowerCase().replace(/\s/g, "-").replace(/ı/g, "i").replace(/ü/g, "u")}`}
              className={`bento-card ${cat.span} relative overflow-hidden group border border-slate-800 bg-slate-900/30 backdrop-blur-sm hover:border-amber-500/50 transition-all duration-500 ${visible ? "fade-in-up" : ""}`}
              style={{ minHeight: cat.span.includes("row-span-2") ? "480px" : "280px" }}
            >
              {/* Image */}
              <img
                src={cat.img}
                alt={cat.name}
                className="absolute inset-0 w-full h-full object-cover img-zoom opacity-40 group-hover:opacity-50 transition-opacity duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent z-[2]" />

              {/* Content */}
              <div className="relative z-[3] flex flex-col justify-end h-full p-8 md:p-10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 border border-amber-500/40 flex items-center justify-center text-amber-500">
                    {cat.icon}
                  </div>
                </div>
                <h3
                  className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-50 mb-2"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  {cat.name}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed max-w-md">
                  {cat.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ===== BRANDS SECTION ===== */
const Brands = () => (
  <section
    id="brands"
    data-testid="brands-section"
    className="py-24 md:py-32 border-y border-slate-800/50 overflow-hidden"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 mb-16">
      <p className="text-xs font-bold tracking-[0.25em] uppercase text-amber-500 mb-4">
        Güvenilir Markalar
      </p>
      <h2
        className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-50"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        Türkiye'nin En İyi Markaları
      </h2>
    </div>

    {/* Marquee */}
    <div className="relative">
      <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-slate-950 to-transparent z-10" />
      <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-slate-950 to-transparent z-10" />

      <div className="overflow-hidden">
        <div className="marquee-track">
          {[...BRANDS, ...BRANDS].map((brand, i) => (
            <span
              key={`${brand}-${i}`}
              data-testid={i < BRANDS.length ? `brand-${brand.toLowerCase().replace(/\s/g, "-")}` : undefined}
              className="brand-item inline-block px-12 md:px-16 text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter uppercase text-slate-700 whitespace-nowrap select-none"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {brand}
            </span>
          ))}
        </div>
      </div>
    </div>

    {/* Second row - reverse direction */}
    <div className="relative mt-6">
      <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-slate-950 to-transparent z-10" />
      <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-slate-950 to-transparent z-10" />

      <div className="overflow-hidden">
        <div className="marquee-track" style={{ animationDirection: "reverse", animationDuration: "35s" }}>
          {[...BRANDS.slice().reverse(), ...BRANDS.slice().reverse()].map((brand, i) => (
            <span
              key={`rev-${brand}-${i}`}
              className="brand-item inline-block px-12 md:px-16 text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter uppercase text-slate-800 whitespace-nowrap select-none"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {brand}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ===== CONTACT FORM ===== */
const ContactForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", phone: "", message: "" });
      setTimeout(() => setStatus("idle"), 4000);
    } catch {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 3000);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
      <div>
        <label htmlFor="name" className="block text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">
          Adınız
        </label>
        <input
          id="name"
          data-testid="contact-form-name"
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full bg-transparent border border-slate-800 px-4 py-3 text-slate-200 placeholder-slate-600 focus:border-amber-500/50 focus:outline-none transition-colors"
          placeholder="Adınızı giriniz"
          required
        />
      </div>
      <div>
        <label htmlFor="phone" className="block text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">
          Telefon
        </label>
        <input
          id="phone"
          data-testid="contact-form-phone"
          type="tel"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          className="w-full bg-transparent border border-slate-800 px-4 py-3 text-slate-200 placeholder-slate-600 focus:border-amber-500/50 focus:outline-none transition-colors"
          placeholder="05XX XXX XX XX"
          required
        />
      </div>
      <div>
        <label htmlFor="message" className="block text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">
          Mesajınız
        </label>
        <textarea
          id="message"
          data-testid="contact-form-message"
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          rows={4}
          className="w-full bg-transparent border border-slate-800 px-4 py-3 text-slate-200 placeholder-slate-600 focus:border-amber-500/50 focus:outline-none transition-colors resize-none"
          placeholder="Mesajınızı yazınız..."
          required
        />
      </div>
      <button
        type="submit"
        data-testid="contact-form-submit"
        disabled={status === "loading" || status === "success"}
        className={`inline-flex items-center gap-2 font-bold px-8 py-4 uppercase tracking-wider text-sm transition-all duration-300 ${
          status === "success"
            ? "bg-green-600 text-white"
            : "bg-amber-600 text-slate-950 hover:bg-amber-500"
        } disabled:opacity-70`}
      >
        {status === "loading" && <Loader2 className="w-4 h-4 animate-spin" />}
        {status === "success" && <CheckCircle className="w-4 h-4" />}
        {status === "idle" && <Send className="w-4 h-4" />}
        {status === "error" && <Send className="w-4 h-4" />}
        {status === "loading" ? "Gönderiliyor..." : status === "success" ? "Gönderildi!" : status === "error" ? "Hata! Tekrar Deneyin" : "Mesaj Gönder"}
      </button>
    </form>
  );
};

/* ===== CONTACT / FOOTER ===== */
const Contact = () => (
  <section id="contact" data-testid="contact-section" className="bg-black py-24 md:py-32 border-t border-slate-900">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 md:gap-24">
        {/* Left Side - Info */}
        <div>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-amber-500 mb-6">
            İletişim
          </p>
          <h2
            className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase text-slate-50 leading-[0.9]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            BİZE
            <br />
            ULAŞIN
          </h2>
          <div className="divider-amber mt-8 mb-12" />

          <div className="space-y-8">
            <div data-testid="contact-address" className="group">
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">Adres</p>
                  <p className="text-lg text-slate-300 group-hover:text-slate-50 transition-colors">
                    Kemal Müftüoğlu Sokak, Merkez
                    <br />
                    Hasanbeyli / Osmaniye
                  </p>
                </div>
              </div>
            </div>

            <div data-testid="contact-phone" className="group">
              <div className="flex items-start gap-4">
                <Phone className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">Telefon</p>
                  <a
                    href="tel:+905455369755"
                    className="text-lg text-slate-300 group-hover:text-amber-500 transition-colors"
                  >
                    0545 536 97 55
                  </a>
                </div>
              </div>
            </div>

            <div data-testid="contact-email" className="group">
              <div className="flex items-start gap-4">
                <Mail className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">E-posta</p>
                  <a
                    href="mailto:yigitkahveciogluu.wix.com"
                    className="text-lg text-slate-300 group-hover:text-amber-500 transition-colors break-all"
                  >
                    yigitkahveciogluu.wix.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Contact Form */}
        <div className="flex flex-col justify-center">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-amber-500 mb-6">
            Bize Yazın
          </p>
          <h3
            className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-50 mb-8"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Mesaj Gönderin
          </h3>
          <ContactForm />
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="mt-24 pt-8 border-t border-slate-900 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-xs text-slate-600 tracking-wider">
          &copy; {new Date().getFullYear()} Tuğra Yapı Market. Tüm hakları saklıdır.
        </p>
        <p className="text-xs text-slate-700 tracking-wider">
          tugrayapimarket.com
        </p>
      </div>
    </div>
  </section>
);

/* ===== SCROLL TO TOP ===== */
const ScrollToTop = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!show) return null;

  return (
    <button
      data-testid="scroll-to-top"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-8 right-8 z-50 w-12 h-12 bg-amber-600 hover:bg-amber-500 text-slate-950 flex items-center justify-center transition-all duration-300 shadow-lg shadow-amber-600/20"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
};

/* ===== MAIN APP ===== */
function App() {
  return (
    <div className="bg-slate-950 min-h-screen">
      <Navbar />
      <Hero />
      <About />
      <Categories />
      <Brands />
      <Contact />
      <ScrollToTop />
    </div>
  );
}

export default App;
